import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Send, Paperclip, Phone, Video, Smile, MoreVertical, MessageCircle, Reply, Copy, Trash2, X, Search, Download,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { scrollWithinContainer } from "@/lib/scroll";

const QUICK_EMOJI = ["👍", "❤️", "🎉", "🔥", "🚀", "👀", "✅", "💡", "🙏", "😂", "😮", "😢"];

function MessageMenu({ canDelete, onReply, onCopy, onDelete }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const h = () => setOpen(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)} className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/10 ${canDelete ? "text-white/80" : "text-kb-ink-muted"}`} data-testid="msg-menu-btn">
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute z-10 right-0 mt-1 bg-white border border-slate-200 shadow-lg rounded-sm py-1 text-xs text-kb-ink w-32">
          <button onClick={() => { onReply(); setOpen(false); }} className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2">
            <Reply className="w-3 h-3" /> Reply
          </button>
          <button onClick={() => { onCopy(); setOpen(false); }} className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2">
            <Copy className="w-3 h-3" /> Copy
          </button>
          {canDelete && (
            <button onClick={() => { onDelete(); setOpen(false); }} className="w-full px-3 py-1.5 text-left hover:bg-rose-50 text-rose-600 flex items-center gap-2">
              <Trash2 className="w-3 h-3" /> Delete for everyone
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HeaderMenu({ onSearch, onClear }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const h = () => setOpen(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)} className="p-2 hover:bg-white/10 rounded-full transition-colors" title="More" data-testid="chat-header-menu">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute z-10 right-0 mt-1 bg-white border border-slate-200 shadow-lg rounded-sm py-1 text-xs text-kb-ink w-44">
          <button onClick={() => { onSearch(); setOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"><Search className="w-3 h-3" /> Search messages</button>
          <button onClick={() => { onClear(); setOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"><X className="w-3 h-3" /> Clear filter</button>
        </div>
      )}
    </div>
  );
}

export default function ProjectChat({ ideaId, workspaceId, onStartVideoCall, onStartAudioCall, statusLine }) {
  // Unified: prefer workspaceId (new callers), fall back to ideaId (legacy).
  const wsid = workspaceId || ideaId;
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/workspace/${wsid}/chat`);
      setMessages(r.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [wsid]);

  useEffect(() => {
    scrollWithinContainer(endRef.current);
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    if ((!text.trim() && !pendingAttachment) || sending) return;
    setSending(true);
    try {
      await api.post(`/workspace/${wsid}/chat`, {
        body: text.trim(),
        reply_to_id: reply?.id || null,
        attachment: pendingAttachment,
      });
      setText("");
      setReply(null);
      setPendingAttachment(null);
      load();
    } catch (err) {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await uploadAttachment(f);
    e.target.value = "";
  };

  // Shared uploader — used by the file picker AND the paste handler.
  const uploadAttachment = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB per attachment (use the Drive tab for larger files)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/workspace/${wsid}/chat/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingAttachment(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Paste-to-attach: any pasted image from the clipboard (screenshot,
  // copied image) becomes an attachment on the next send.
  const onPasteText = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) {
          e.preventDefault();
          const named = new File([blob], `pasted-${Date.now()}.${(it.type.split("/")[1] || "png")}`, { type: it.type });
          uploadAttachment(named);
          toast.info("Image pasted — attach preview below the input");
          return;
        }
      }
    }
  };

  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/workspace/${wsid}/chat/${m.id}`);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const insertEmoji = (emo) => {
    setText((t) => t + emo);
    setEmojiOpen(false);
    inputRef.current?.focus();
  };

  const visibleMessages = search ? messages.filter((m) => (m.body || "").toLowerCase().includes(search.toLowerCase())) : messages;
  const messagesById = Object.fromEntries(messages.map((m) => [m.id, m]));

  return (
    <div className="kb-card flex flex-col overflow-hidden" style={{ height: "min(70vh, 720px)" }} data-testid="project-chat">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-kb-ink text-white">
        <div className="w-10 h-10 rounded-full bg-kb-primary/30 flex items-center justify-center">
          <MessageCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Project chat</div>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-white/60 truncate">
            {statusLine || "Group conversation"}
          </div>
        </div>
        <button onClick={onStartAudioCall} className="p-2 hover:bg-white/10 rounded-full transition-colors" title="Start audio call (Daily.co · in-app — everyone here joins the same room)" data-testid="chat-audio-call-btn">
          <Phone className="w-4 h-4" />
        </button>
        <button onClick={onStartVideoCall} className="p-2 hover:bg-white/10 rounded-full transition-colors" title="Start video call (Daily.co · in-app — everyone here joins the same room)" data-testid="chat-video-call-btn">
          <Video className="w-4 h-4" />
        </button>
        <HeaderMenu onSearch={() => setShowSearch((v) => !v)} onClear={() => { setSearch(""); setShowSearch(false); }} />
      </div>

      {showSearch && (
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex gap-2">
          <Search className="w-4 h-4 text-kb-ink-muted self-center" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search messages…" className="kb-input text-sm flex-1" autoFocus data-testid="chat-search-input" />
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
        style={{
          background:
            "radial-gradient(rgba(122,68,252,0.04) 1px, transparent 1px), radial-gradient(rgba(122,68,252,0.04) 1px, #faf9ff 1px)",
          backgroundSize: "20px 20px, 20px 20px",
          backgroundPosition: "0 0, 10px 10px",
        }}
        data-testid="project-chat-scroll"
      >
        {visibleMessages.length === 0 ? (
          <div className="text-center text-sm text-kb-ink-muted py-10">
            <MessageCircle className="w-8 h-8 mx-auto text-kb-ink-light mb-3" strokeWidth={1.2} />
            {search ? "No messages match your search." : "No messages yet — start the conversation."}
          </div>
        ) : (
          visibleMessages.map((m, idx) => {
            const mine = m.user_id === user?.user_id;
            const prev = visibleMessages[idx - 1];
            const sameAuthor = prev && prev.user_id === m.user_id;
            const replyMsg = m.reply_to_id ? messagesById[m.reply_to_id] : null;
            const a = m.attachment;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`project-chat-msg-${m.id}`}>
                <div className={`max-w-[78%] rounded-lg px-3 py-2 shadow-sm ${
                  mine ? "bg-kb-primary text-white rounded-br-sm" : "bg-white border border-slate-200 text-kb-ink rounded-bl-sm"
                }`}>
                  {!mine && !sameAuthor && (
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mb-1">
                      {m.user_name} {m.user_role && <span className="text-kb-ink-light">· {m.user_role}</span>}
                    </div>
                  )}
                  {replyMsg && (
                    <div className={`text-[11px] border-l-2 pl-2 mb-1.5 ${mine ? "border-white/40 text-white/80" : "border-kb-primary/40 text-kb-ink-muted"}`}>
                      <div className={`kb-mono text-[9px] uppercase tracking-wider ${mine ? "text-white/70" : "text-kb-primary"}`}>{replyMsg.user_name}</div>
                      <div className="line-clamp-2">{replyMsg.body || "📎 attachment"}</div>
                    </div>
                  )}
                  {a && a.kind === "image" && (
                    <div className="mb-1 relative group/img">
                      <a href={a.url} target="_blank" rel="noreferrer" className="block">
                        <img src={a.url} alt={a.name || "image"} className="max-w-full rounded-sm border border-black/5" style={{ maxHeight: 240 }} />
                      </a>
                      <a href={a.url} download={a.name || "image.png"} title="Download"
                        className="absolute top-1.5 right-1.5 opacity-0 group-hover/img:opacity-100 transition-opacity inline-flex items-center gap-1 bg-black/60 hover:bg-black/80 text-white text-[10px] kb-mono px-2 py-1 rounded-sm"
                        data-testid={`chat-img-download-${a.name || "img"}`}
                      >
                        <Download className="w-3 h-3" /> Save
                      </a>
                    </div>
                  )}
                  {a && a.kind === "pdf" && (
                    <a href={a.url} target="_blank" rel="noreferrer" className={`flex items-center gap-2 text-xs mb-1 underline ${mine ? "text-white/90" : "text-kb-primary"}`}>
                      <Paperclip className="w-3 h-3" /> {a.name} (PDF) — open inline
                    </a>
                  )}
                  {a && a.kind === "file" && (
                    <a href={a.url} download={a.name} className={`flex items-center gap-2 text-xs mb-1 underline ${mine ? "text-white/90" : "text-kb-primary"}`}>
                      <Paperclip className="w-3 h-3" /> {a.name}
                    </a>
                  )}
                  {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                  <div className="flex items-center gap-2 mt-1 justify-end">
                    <div className={`text-[10px] tabular-nums ${mine ? "text-white/70" : "text-kb-ink-light"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <MessageMenu
                      canDelete={
                        mine ||
                        ["admin", "super_admin"].includes(user?.role) ||
                        (user?.secondary_roles || []).some((r) => ["admin", "super_admin"].includes(r))
                      }
                      onReply={() => { setReply(m); inputRef.current?.focus(); }}
                      onCopy={() => { navigator.clipboard.writeText(m.body || ""); toast.success("Copied"); }}
                      onDelete={() => deleteMsg(m)}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Reply / attachment preview */}
      {(reply || pendingAttachment) && (
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 flex items-center gap-2 text-xs">
          {reply && (
            <div className="flex-1 min-w-0 border-l-2 border-kb-primary pl-2">
              <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary">REPLYING TO {reply.user_name}</div>
              <div className="text-kb-ink-muted line-clamp-1">{reply.body || "📎 attachment"}</div>
            </div>
          )}
          {pendingAttachment && (
            <div className="flex items-center gap-2 text-kb-ink-muted">
              <Paperclip className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{pendingAttachment.name}</span>
            </div>
          )}
          <button onClick={() => { setReply(null); setPendingAttachment(null); }} className="text-rose-500 ml-auto">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Emoji picker */}
      {emojiOpen && (
        <div className="px-3 py-2 border-t border-slate-200 bg-white flex gap-1.5 flex-wrap" data-testid="emoji-picker">
          {QUICK_EMOJI.map((emo) => (
            <button key={emo} onClick={() => insertEmoji(emo)} className="text-lg hover:scale-125 transition-transform p-1">
              {emo}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form onSubmit={send} className="flex items-center gap-1.5 px-3 py-3 border-t border-slate-200 bg-white" data-testid="project-chat-form">
        <button type="button" onClick={() => setEmojiOpen((v) => !v)} className="p-2 text-kb-ink-muted hover:text-kb-primary" title="Emoji">
          <Smile className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 text-kb-ink-muted hover:text-kb-primary" title="Attach file (max 5MB)" data-testid="project-chat-attach">
          <Paperclip className="w-5 h-5" />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={onAttach} />
        <input
          ref={inputRef}
          className="kb-input flex-1 text-sm rounded-full"
          placeholder={uploading ? "Uploading…" : "Type a message (paste an image to attach)…"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={onPasteText}
          data-testid="project-chat-input"
        />
        <button type="submit" disabled={(!text.trim() && !pendingAttachment) || sending} className="p-3 bg-kb-primary text-white rounded-full hover:scale-105 transition-transform disabled:opacity-40 disabled:hover:scale-100" data-testid="project-chat-send">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
